import { useParams } from "@solidjs/router";
import { createSignal, onMount, Show } from "solid-js";
import { User } from "../components/UserContextProvider";
import { useSFetch } from "../hooks/sfetch";
import { useApi, isOk } from "../api";
import { useRoleParser } from "../hooks/permissions";
import { For } from "solid-js";
import Text from "../components/Text";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import { createMemo } from "solid-js"; 
import { useNavigate } from "@solidjs/router";
import AddonState from "../components/AddonState";
import { formatDateTime } from "../services/dateFormat";
import UserGrantsAdmin from "../components/UserGrantsAdmin";
import { useUserContext } from "../components/UserContextProvider";
import StateDisplay from "../components/StateDisplay";

export function UserPage() {

    const [resetPassword, setResetPassword] = createSignal("");
    const { parseRoles, roles } = useRoleParser();
    const params = useParams();
    const sfetch = useSFetch();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user: currentUser } = useUserContext();
    const isRoot = () => parseRoles(currentUser()?.permissions ?? 0, roles)[roles.indexOf('Root')];

    const [enabledClasses, setEnabledClasses] = createSignal<string[]>([]);
    const [editEnabledClasses, setEditEnabledClasses] = createSignal<string[]>([]);
    const [allClasses, setAllClasses] = createSignal<string[]>([]);
    const [permissions, setPermissions] = createSignal<boolean[]>([false, false, false]);

    const [openPackages, setOpenPackages] = createSignal<Record<string, boolean>>({});

    function togglePackage(pkg: string) {
        setOpenPackages(o => ({ ...o, [pkg]: !(o[pkg] ?? true) }));
    }

    const [user, setUser] = createSignal<User>({
        username: "",
        id: "",
        language: {},
        loggedin: false
    });
    const [loading, setLoading] = createSignal(true);
    const [loadErrorKey, setLoadErrorKey] = createSignal<string | null>(null);

    async function fetchUser() {
        setLoading(true);
        setLoadErrorKey(null);
        try {
            // GetUserById returns the raw user object on success (not the usual ApiEnvelope), so a
            // synthesized envelope status of 200 doesn't apply here - a present id/username is the
            // only reliable success signal. 403 is set by sfetch itself before that synthesis runs.
            const response = await sfetch(`/api/User/${params.id}`);
            if (response.status === 403 || typeof response.id !== "string" || !response.id) {
                setLoadErrorKey(response.status === 403 ? "USER_NO_ACCESS" : "USER_FETCH_ERROR");
                setLoading(false);
                return;
            }
            setUser(response);
            setPermissions(parseRoles(response.permissions ?? 0, roles));
        } catch (_error) {
            setLoadErrorKey("USER_FETCH_ERROR");
            setLoading(false);
            return;
        }
        setLoading(false);

        const classNamesRes = await api.jarDownload.getAllClassNames();
        if (isOk(classNamesRes.data)) {
            setAllClasses(classNamesRes.data.value?.classNames ?? []);
        } else {
            toast?.error(t("USER_FETCH_CLASSES_ERROR"));
        }

        await sfetch(`/api/User/EnabledClasses/${params.id}`).then(data => {
            if (data.status == 200) {
                setEnabledClasses(data.value);
                setEditEnabledClasses(data.value);
            }
            else {
                toast?.error(t("USER_FETCH_ENABLED_CLASSES_ERROR"));
            }
        });
    }

    onMount(fetchUser);

    function setPermissionAtIndex(index: number, e: Event) {
        const newPermissions = [...permissions()];
        newPermissions[index] = (e.target as HTMLInputElement).checked;
        setPermissions(newPermissions);
    }

    function permissionInteger(roles: boolean[]) : number {
        let permissionInt = 0;
        roles.forEach((role, i) => {
            if (role) {
                permissionInt |= 1 << i;
            }
        });
        return permissionInt;
    }
    
    function save(e: Event){
        e.preventDefault();
        sfetch(`/api/User/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                permissions: permissionInteger(permissions()),
                id: user().id,
                versionNumber: user().versionNumber
            })
        }).then(data => {
            if (data.status == 200) {
                fetchUser();
                toast?.info(t("USER_PERMISSIONS_UPDATED"));
            }
            else {
                toast?.error(t("USER_PERMISSIONS_UPDATE_ERROR"));
            }
        });
    }

    function resetPasswordAsAdmin(e: Event){
        e.preventDefault();
        sfetch(`/api/User/ChangePasswordAdmin/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userId: user().id,
                newPassword: resetPassword(),
                newPasswordRepeated: resetPassword()
            })
        }).then(data => {
            if (data.status == 200) {
                toast?.info(t("USER_PASSWORD_RESET"));
            }
            else {
                toast?.error(t("USER_PASSWORD_RESET_ERROR"));
            }
        });
    }

    function logoutGlobal(e: Event){
        e.preventDefault();
        sfetch(`/api/User/LogoutUserGlobal/` + user().id, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(data => {
            if (data.status == 200) {
                toast?.info(t("USER_LOGGED_OUT"));
            }
            else {
                toast?.error(t("USER_LOGOUT_ERROR"));
            }
        });
    }

    function saveClasses() {
        sfetch(`/api/User/EnabledClasses`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                enabledClasses: editEnabledClasses(),
                userId: user().id
            })
        }).then(data => {
            if (data.status == 200) {
                toast?.info(t("USER_CLASSES_UPDATED"));
                setEnabledClasses(editEnabledClasses());
            }
            else {
                toast?.error(t("USER_CLASSES_UPDATE_ERROR"));
            }
        });
    }

    function logoutSession(id: string) {
        sfetch("/api/User/Session/" + id, {
            method: "DELETE"
        }).then(() => {
            toast?.info(t("USER_SESSION_LOGGED_OUT"));
        }).catch((_error) => {
            toast?.error(t("USER_LOGOUT_ERROR"));
        });
    }

    function regenerateaddonkey() {
        sfetch("/api/User/RegenerateAddonKey/", {
            method: "POST",
            body: JSON.stringify({
                userId: user().id
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(() => {
            toast?.info(t("USER_ADDON_KEY_REGENERATED"));
        }).catch((error) => {
            toast?.error(t("USER_ADDON_KEY_REGENERATE_ERROR") + error);
        });
    }

    const reactiveAllClasses = createMemo(() => allClasses().map(value => ({
        value,
        checked: editEnabledClasses().includes(value),
    })));

    const groupedClasses = createMemo(() => {
        const grouped: Record<string, { value: string, checked: boolean }[]> = {};
        for (const { value: fqcn, checked } of reactiveAllClasses()) {
            const lastDot = fqcn.lastIndexOf(".");
            const pkg = lastDot > 0 ? fqcn.substring(0, lastDot) : "(default)";
            if (!grouped[pkg]) grouped[pkg] = [];
            grouped[pkg].push({ value: fqcn, checked });
        }
        return grouped;
    });

    return (
        <div class="dashboard-page">
            <Show when={loading()}>
                <StateDisplay loading />
            </Show>
            <Show when={!loading() && loadErrorKey()}>
                <StateDisplay
                    error
                    icon="fa-solid fa-user-slash"
                    message={loadErrorKey()!}
                    onRetry={fetchUser}
                />
            </Show>
            <Show when={!loading() && !loadErrorKey()}>
            <div class="h wrap">
                <div class="v align-center">
                    <h2 class="username mc">{user().username}</h2>
                    <img class="minecraft-avatar" style={{ width: "7rem", height: "14rem" }} src={`https://mineskin.eu/armor/body/${user().minecraftUUID}/100.png`} alt={t("USER_AVATAR_ALT")}/>
                    <Show when={user()?.id !== ""}>
                        <AddonState id={user().id} />
                        <h3><Text val="TOTAL_ADDON_TIME" /></h3>
                        <span class="font-mono">{t("USER_HOURS", user().fifteenMinutesInAddonCount ? (user().fifteenMinutesInAddonCount! / 4) : 0)}</span>
                    </Show>
                </div>
                <div class="v" style={{ flex: "1" }}>
                    <div class="fcard v">
                        <h2><Text val="USER_DETAILS" /></h2>
                        <div class="field-group">
                            <span class="field-label"><Text val="USER_ID" /></span>
                            <span class="field-value">{user().id}</span>
                        </div>
                        <div class="field-group">
                            <span class="field-label"><Text val="STATUS" /></span>
                            <span class="field-value">{user().minecraftUsernameVerified ? <Text val="VERIFIED" /> : <Text val="NOT_VERIFIED" />}</span>
                        </div>
                        <div class="hline"></div>
                        <div class="h wrap">
                            <For each={roles}>
                                {(role, i) => (
                                    <div class="permission-item">
                                        <label>
                                            <input type="checkbox" onChange={(e: any) => setPermissionAtIndex(i(), e)} checked={permissions()[i()]} />
                                            {role}
                                        </label>
                                    </div>
                                )}
                            </For>
                        </div>
                        <button onClick={save}><Text val="SAVECHANGES"></Text></button>
                    </div>
                    <div class="h wrap">
                        <div class="fcard v">
                            <h3><Text val="USER_SECURITY_HEADING" /></h3>
                            <input type="password" placeholder={t("PASSWORD")} value={resetPassword()} onInput={(e: any) => setResetPassword(e.target.value)} />
                            <button class="danger" onClick={resetPasswordAsAdmin}><Text val="RESETPASSWORD"></Text></button>
                        </div>
                        <div class="fcard v">
                            <h3><Text val="USER_ACCOUNT_CONTROL_HEADING" /></h3>
                            <button class="danger" onClick={logoutGlobal}><Text val="LOGOUTGLOBAL"></Text></button>
                            <button class="danger" onClick={regenerateaddonkey}><Text val="REGENERATEADDONKEY"></Text></button>
                            <button onClick={() => navigate("/auditlogs/" + user().id)}><Text val="AUDITLOGS"></Text></button>
                        </div>
                    </div>
                </div>
            </div>
            <Show when={isRoot() && user().id !== ''}>
                <UserGrantsAdmin userId={user().id} />
            </Show>
            <div class="fcard v">
                <h2><Text val="SESSIONS"></Text></h2>
                <table class='sessiontable'>
                    <thead>
                        <tr>
                            <th><Text val="VALID"></Text></th>
                            <th><Text val="LOCATION"></Text></th>
                            <th><Text val="CREATEDAT"></Text></th>
                            <th><i class="fa-solid fa-trash"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={user().sessions}>
                            {(session) => (
                                <tr>
                                    <td>{session.isVerifiedAndValid ? <i class="fa-solid fa-check i g"></i> : <i class="fa-solid fa-xmark i r"></i>}</td>
                                    <td>{session.info}</td>
                                    <td>{formatDateTime(session.createdAt)}</td>
                                    <td><button class='danger' onClick={() => logoutSession(session.id)}><i class="fa-solid fa-trash"></i></button></td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>
            <div class="fcard v">
                <div class="h wrap">
                    <div class="classescon">
                        <h2><Text val="ENABLED_CLASSES" /></h2>
                        <div class="row">
                            <button onClick={() => {
                                setEditEnabledClasses(allClasses());
                            }}><Text val="SELECTALL"></Text></button>
                            <button onClick={() => {
                                setEditEnabledClasses([]);
                            }}><Text val="DESELECTALL"></Text></button>
                            <button onClick={() => {
                                setEditEnabledClasses(enabledClasses());
                            }}><Text val="RESET"></Text></button>
                        </div>
                        <For each={Object.entries(groupedClasses())}>
                        {([pkg, classes]) => (
                            <div style={{ "margin-bottom": "var(--spacing-lg)" }}>
                            <div
                                style={{ cursor: "pointer", "user-select": "none" }}
                                onClick={() => togglePackage(pkg)}
                            >
                                <span style={{ "font-weight": "bold", "margin-right": "var(--spacing-sm)" }}>
                                {openPackages()[pkg] ?? true ? "▼" : "►"}
                                </span>
                                <strong>{pkg}</strong>
                            </div>
                            {(openPackages()[pkg] ?? true) && (
                                <div style={{ "margin-left": "var(--spacing-xl)" }}>
                                <For each={classes}>
                                    {value => (
                                    <label style={{ display: "block" }}>
                                        <input
                                        type="checkbox"
                                        checked={value.checked}
                                        onChange={e => {
                                            if (e.target.checked) {
                                            setEditEnabledClasses([...editEnabledClasses(), value.value]);
                                            } else {
                                            setEditEnabledClasses(
                                                editEnabledClasses().filter(v => v !== value.value)
                                            );
                                            }
                                        }}
                                        />
                                        {value.value.substring(value.value.lastIndexOf(".") + 1)}
                                    </label>
                                    )}
                                </For>
                                </div>
                            )}
                            </div>
                        )}
                        </For>

                    </div>
                    <div>
                        <h2><Text val="CURRENT_DIFF" /></h2>
                        <h3><Text val="ADDED_CLASSES" /></h3>
                        <For each={editEnabledClasses().filter((v) => !enabledClasses().includes(v))}>
                        {(value) => (
                                <div class="class-item">
                                    <label>
                                        <span class="green nom">+ {value}</span>
                                    </label>
                                </div>
                            )}
                        </For>
                        <h3><Text val="REMOVED_CLASSES" /></h3>
                        <For each={enabledClasses().filter((v) => !editEnabledClasses().includes(v))}>
                        {(value) => (
                                <div class="class-item">
                                    <label>
                                        <span class="red nom">- {value}</span>
                                    </label>
                                </div>
                            )}
                        </For>
                        <button onClick={saveClasses}><Text val="SAVECHANGES"></Text></button>
                    </div>
                </div>
            </div>
            </Show>
        </div>
    );
    
}
